import { isDateToken } from './dates';
import type { Param, ParamValue, PeriodUnit, Range } from './types';

const RANGE_BODY = /^(\d*)(-?)(\d*)$/;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Returns the value `token` sets for `param`, or undefined if it doesn't match. */
export function matchToken(
  param: Param,
  token: string
): ParamValue | undefined {
  switch (param.type) {
    case 'flag':
      return token === param.token || param.aliases?.includes(token)
        ? true
        : undefined;
    case 'choice':
      return param.options.find(
        o => o.token === token || o.aliases?.includes(token)
      )?.value;
    case 'range': {
      if (!token.startsWith(param.prefix)) return undefined;
      const m = RANGE_BODY.exec(token.slice(param.prefix.length));
      if (!m) return undefined;
      const [, a = '', dash, b = ''] = m;
      if (!a && !b) return undefined;
      // `$200` alone reads as "up to 200".
      if (!dash) return { max: Number(a) };
      const range: Range = {};
      if (a) range.min = Number(a);
      if (b) range.max = Number(b);
      if (
        range.min !== undefined &&
        range.max !== undefined &&
        range.min > range.max
      )
        return undefined;
      return range;
    }
    case 'number': {
      const re = new RegExp(
        `^${escapeRegExp(param.prefix ?? '')}(\\d+)${escapeRegExp(param.suffix ?? '')}$`
      );
      const m = re.exec(token);
      return m ? Number(m[1]) : undefined;
    }
    case 'text': {
      if (!token.startsWith(param.prefix)) return undefined;
      const body = token.slice(param.prefix.length);
      return body && param.pattern.test(body) ? body : undefined;
    }
    case 'dates':
      return isDateToken(token) ? token : undefined;
    case 'period': {
      const m = /^([hdwmy])([1-9]\d*)?$/.exec(token);
      if (!m) return undefined;
      const unit = m[1] as PeriodUnit;
      if (!param.units.includes(unit)) return undefined;
      if (m[2] && (!param.counted || m[2] === '0')) return undefined;
      // `d1` and `d` mean the same thing; normalise to the short form.
      return m[2] === '1' ? unit : token;
    }
  }
}

export function isEmptyValue(value: ParamValue | undefined): boolean {
  if (value === undefined || value === false || value === '') return true;
  if (typeof value === 'object')
    return value.min === undefined && value.max === undefined;
  return false;
}

/** Token for a param value, or undefined when the value is empty. */
export function formatValue(
  param: Param,
  value: ParamValue | undefined
): string | undefined {
  if (isEmptyValue(value)) return undefined;
  switch (param.type) {
    case 'flag':
      return value === true ? param.token : undefined;
    case 'choice':
      return param.options.find(o => o.value === value)?.token;
    case 'range': {
      if (typeof value !== 'object') return undefined;
      const { min, max } = value;
      if (min === undefined) return `${param.prefix}${max}`;
      return `${param.prefix}${min}-${max ?? ''}`;
    }
    case 'number':
      return typeof value === 'number'
        ? `${param.prefix ?? ''}${value}${param.suffix ?? ''}`
        : undefined;
    case 'text':
    case 'period':
    case 'dates':
      return typeof value === 'string'
        ? `${'prefix' in param ? param.prefix : ''}${value}`
        : undefined;
  }
}

/** Every literal token a param claims; used to check for collisions. */
export function literalTokens(param: Param): string[] {
  switch (param.type) {
    case 'flag':
      return [param.token, ...(param.aliases ?? [])];
    case 'choice':
      return param.options.flatMap(o => [o.token, ...(o.aliases ?? [])]);
    default:
      return [];
  }
}

/** Splits a period token into unit and count: `m6` -> ['m', 6]. */
export function parsePeriod(token: string): [PeriodUnit, number] {
  const unit = token[0] as PeriodUnit;
  const count = token.length > 1 ? Number(token.slice(1)) : 1;
  return [unit, count];
}

/** A short example token for a param, used in help text. */
export function exampleToken(param: Param): string {
  switch (param.type) {
    case 'flag':
      return param.token;
    case 'choice':
      return param.options.map(o => o.token).join(' | ');
    case 'range':
      return `${param.prefix}min-max`;
    case 'number':
      return `${param.prefix ?? ''}${param.placeholder ?? 'n'}${param.suffix ?? ''}`;
    case 'text':
      return `${param.prefix}${param.placeholder ?? 'value'}`;
    case 'dates':
      return '10/17-10/19 | wknd | tmrw | +7';
    case 'period':
      return `${param.units.join(' | ')}${param.counted ? '  (+count, e.g. m6)' : ''}`;
  }
}
